/**
 * Voice Settings Popover
 *
 * Quick-access popover for text-to-speech voice settings:
 * - Reading Speed
 * - Voice Selection
 * - Status Indicator
 */

import { Volume2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Slider } from '@/components/ui/slider'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useTextToSpeech } from '@/contexts/TextToSpeechContext'
import { useReadAloud } from '@/contexts/ReadAloudContext'

interface VoiceSettingsPopoverProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children?: React.ReactNode
  showTTSToolbar?: boolean
  onToggleTTSToolbar?: (enabled: boolean) => void
}

export function VoiceSettingsPopover({
  open,
  onOpenChange,
  children,
  showTTSToolbar = false,
  onToggleTTSToolbar,
}: VoiceSettingsPopoverProps) {
  const { rate, selectedVoice, setRate, setVoice, getQualityVoices } = useTextToSpeech()
  const { isReadingPage, isPaused } = useReadAloud()

  const qualityVoices = getQualityVoices()

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {children || (
          <Button variant="ghost" size="icon" className="h-8 w-8" title="Voice settings">
            <Volume2 className="w-4 h-4" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-80" align="center" side="top">
        <div className="space-y-4">
          {/* Header */}
          <div className="space-y-1">
            <h4 className="text-sm font-semibold">Voice Settings</h4>
            <p className="text-xs text-muted-foreground">Text-to-speech controls</p>
          </div>

          <div className="border-t pt-3 space-y-4">
            {/* Read Aloud Toggle */}
            <div className="flex items-center justify-between">
              <Label htmlFor="voice-read-aloud" className="text-xs font-medium cursor-pointer">
                Read Aloud
              </Label>
              <Switch
                id="voice-read-aloud"
                checked={showTTSToolbar}
                onCheckedChange={onToggleTTSToolbar}
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Shows floating playback controls for reading page content
            </p>

            {/* Show voice settings only when Read Aloud is enabled */}
            {showTTSToolbar && (
              <>
                <div className="border-t pt-3" />

                {/* Reading Speed */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="voice-speed" className="text-xs font-medium">
                      Speed
                    </Label>
                    <span className="text-xs text-muted-foreground">{rate.toFixed(1)}x</span>
                  </div>
                  <Slider
                    id="voice-speed"
                    value={[rate]}
                    onValueChange={([value]) => setRate(value)}
                    min={0.5}
                    max={2.0}
                    step={0.1}
                    className="w-full"
                    aria-label="Reading speed"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>0.5x</span>
                    <span>1.0x</span>
                    <span>2.0x</span>
                  </div>
                </div>

                {/* Voice Selection */}
                {qualityVoices.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="voice-select" className="text-xs font-medium">
                      Voice
                    </Label>
                    <Select value={selectedVoice || ''} onValueChange={setVoice}>
                      <SelectTrigger
                        id="voice-select"
                        className="h-8 text-xs"
                        aria-label="Select voice"
                      >
                        <SelectValue placeholder="Select voice" />
                      </SelectTrigger>
                      <SelectContent>
                        {qualityVoices.map((voice) => (
                          <SelectItem key={voice.name} value={voice.name} className="text-xs">
                            {voice.name} ({voice.lang})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Only high-quality voices shown</p>
                  </div>
                )}

                {/* Status indicator */}
                <div className="pt-2 border-t">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-block h-2 w-2 rounded-full ${
                        !isReadingPage
                          ? 'bg-gray-400'
                          : isPaused
                            ? 'bg-yellow-500'
                            : 'bg-green-500 animate-pulse'
                      }`}
                    />
                    <span className="text-xs text-muted-foreground">
                      {!isReadingPage ? 'Ready' : isPaused ? 'Paused' : 'Reading...'}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
